import { useState } from "react";
import Avatar from "../common/Avatar";

const reqId = (r) => r?.id ?? r?.request_id;

const formatWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
    ", " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

// A single incoming contact request (D-010). Requester avatar/name/role-pill,
// topic, message (clamped + expandable), timestamp, and Accept / Decline.
// Decline uses an inline confirm (no browser dialog). Responsive: buttons go
// full-width and stack on mobile.
const LawRequestCard = ({ request, onAccept, onDecline, t = (s) => s }) => {
  const [expanded, setExpanded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const requester = request.requester || {};
  const roles = Array.isArray(requester.roles) ? requester.roles : [];
  const id = reqId(request);
  const message = request.message || "";
  const isLong = message.length > 120;

  const doAccept = async () => {
    if (busy) return;
    setBusy(true);
    await onAccept(id);
    setBusy(false);
  };
  const doDecline = async () => {
    if (busy) return;
    setBusy(true);
    await onDecline(id);
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-[12px] border border-[#E6EAF2] p-3.5">
      <div className="flex items-start gap-3">
        <Avatar
          src={requester.profile_picture_url}
          alt={requester.name}
          fallbackText={requester.name}
          size={40}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[14px] text-[#101828] truncate">
              {requester.name || t("Someone")}
            </span>
            {roles.slice(0, 2).map((r) => (
              <span
                key={r}
                className="text-[11px] text-[#1567D2] bg-[#EBF3FF] border border-[#C9E1FF] rounded-full px-1.5 py-0.5"
              >
                {r}
              </span>
            ))}
          </div>
          {request.topic && (
            <p className="text-[12px] text-[#667085] mt-0.5">
              {t("Topic")}: {request.topic}
            </p>
          )}
        </div>
        {request.created_at && (
          <span className="text-[11px] text-[#98A2B3] whitespace-nowrap">
            {formatWhen(request.created_at)}
          </span>
        )}
      </div>

      {message && (
        <div className="mt-2">
          <p className={`text-[13px] text-[#475467] ${expanded ? "" : "line-clamp-2"}`}>
            {message}
          </p>
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-[12px] text-[#2283FF] mt-0.5"
            >
              {expanded ? t("Show less") : t("Show more")}
            </button>
          )}
        </div>
      )}

      {confirming ? (
        <div className="mt-3 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <span className="text-[13px] text-[#475467] flex-1">
            {t("Decline this request?")}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={busy}
              className="flex-1 px-3 py-2 rounded-[10px] border border-[#DDE3EC] text-[#344054] text-[13px] font-medium"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={doDecline}
              disabled={busy}
              className="flex-1 px-3 py-2 rounded-[10px] bg-[#B42318] text-white text-[13px] font-medium disabled:opacity-60"
            >
              {busy ? t("Declining...") : t("Decline")}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={busy}
            className="w-full sm:flex-1 px-3 py-2 rounded-[10px] border border-[#DDE3EC] text-[#344054] text-[13px] font-medium hover:bg-[#F5F7FB]"
          >
            {t("Decline")}
          </button>
          <button
            type="button"
            onClick={doAccept}
            disabled={busy}
            className="w-full sm:flex-1 px-3 py-2 rounded-[10px] text-white text-[13px] font-medium disabled:opacity-60"
            style={{ backgroundColor: "#2283FF" }}
          >
            {busy ? t("Accepting...") : t("Accept")}
          </button>
        </div>
      )}
    </div>
  );
};

export default LawRequestCard;
